import React, { useState, useEffect } from "react"; // Import useState
import PropTypes from "prop-types";
import styles from "./ExchangeTicketModal.module.css";
import { Modal } from "shared/Modal";
import classNames from "classnames";
import Loader from "shared/Loader";
import { useExchangeTicket } from "./useExchangeTicket";
import { Select } from "shared/Select/Select";

/**
 * Modal that opens when the user clicks the exchange button for a ticket.
 */

export function ExchangeTicketModal({
  isOpen,
  onCollapse,
  onClick,
  iboNumber,
  eventId,
  orderId,
  ticket,
  eventName,
}) {
  const { data, isLoading, refetch, isError, exchangeOrder, error } =
    useExchangeTicket({
      iboNumber,
      eventId,
    });

  const [availableTickets, setAvailableTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (data) {
      const filteredTickets = data.tickets.filter(
        (tkt) => String(tkt.cartTypeID) === String(ticket?.cartTypeId)
      );

      setAvailableTickets(filteredTickets);
    }
  }, [data, ticket]);

  useEffect(() => {
    if (data) {
      refetch();
    }
  }, [iboNumber, eventId]);

  useEffect(() => {
    if (isError && error?.message) {
      setErrorMsg(error.message);
    }
  }, [isError, error]);

  const handleExchangeClick = async () => {
    const primaryTicket = {
      eventId: selectedTicket.eventId,
      ticketId: selectedTicket.ticketId,
      isTandem: false,
      location: selectedTicket.location,
      taxGroup: selectedTicket.taxGroup,
    };

    let tandemTicket = null;

    // If the ticket has a tandemTicketId, add the corresponding tandem ticket
    if (selectedTicket.tandemTicketId !== null) {
      const tandem = availableTickets.find(
        (tkt) => tkt.ticketId === selectedTicket.tandemTicketId
      );
      if (tandem) {
        tandemTicket = {
          eventId: tandem.eventId,
          ticketId: tandem.ticketId,
          isTandem: true,
          location: tandem.location,
          taxGroup: tandem.taxGroup,
        };
      }
    }

    const order = {
      ibo: iboNumber,
      oldOrderId: orderId,
      oldTicketId: ticket.ticketId,
      cost: ticket.cost,
      tax: ticket.tax,
      amount: selectedTicket.amount,
      taxRate: selectedTicket.taxRate,
      contactId: ticket.userId,
      newTickets: tandemTicket
        ? [primaryTicket, tandemTicket]
        : [primaryTicket],
    };

    try {
      await exchangeOrder.mutateAsync(order);
      onClick();
    } catch (error) {
      console.error("Exchange failed:", error);
      setErrorMsg(error.message);
    }
  };

  const selectedTax = selectedTicket
    ? selectedTicket.amount * selectedTicket.taxRate
    : 0;

  const selectedTotal = selectedTicket
    ? selectedTicket.amount + selectedTax
    : 0;

  return (
    <div className={styles.container}>
      <Modal
        title={`Exchange Ticket`}
        isOpen={isOpen}
        onClose={onCollapse}
        className={styles.modal}
      >
        <>
          {isLoading ? (
            <Loader />
          ) : (
            <>
              {errorMsg && <div className={styles.danger}>{errorMsg}</div>}

              {!errorMsg && (
                <>
                  <div className={styles.confirm}>
                    Exchange {eventName} {ticket.ticketName} ticket for:
                  </div>

                  {availableTickets.filter((item) => {
                    if (item.eventName === eventName) {
                      return item.title !== ticket.ticketName;
                    }
                    return true;
                  }).length === 0 ? (
                    <div className={styles.danger}>
                      No available tickets to exchange.
                    </div>
                  ) : (
                    <>
                      <div className={styles.dropdown}>
                        <Select
                          options={[
                            ...availableTickets
                              .filter((item) =>
                                item.eventName === eventName
                                  ? item.title !== ticket.ticketName
                                  : true
                              )
                              .map((tkt) => `${tkt.eventName}: ${tkt.title}`),
                          ]}
                          placeholder={
                            selectedTicket
                              ? `${selectedTicket.eventName}: ${selectedTicket.title}`
                              : "Select"
                          }
                          onChange={(option) => {
                            const [eventName, title] = option.split(": ");
                            setSelectedTicket(
                              availableTickets.find(
                                (tkt) =>
                                  tkt.title === title &&
                                  tkt.eventName === eventName
                              )
                            );
                          }}
                        />
                      </div>

                      {selectedTicket && (
                        <>
                          {ticket.host &&
                            selectedTicket.host &&
                            ticket.host !== selectedTicket.host && (
                              <div className={styles.danger}>
                                This ticket is in different line of sponsorship
                                than the original
                              </div>
                            )}
                          <div className={styles.priceSummaryContainer}>
                            <table className={styles.priceSummaryTable}>
                              <thead>
                                <tr>
                                  <th></th>
                                  <th>Cost</th>
                                  <th>Tax</th>
                                  <th>Total</th>
                                </tr>
                              </thead>
                              <tbody className={styles.tableBody}>
                                <tr className={styles.labelCell}>
                                  <td>Current Ticket</td>
                                  <td>${ticket.cost.toFixed(2)}</td>
                                  <td>${ticket.tax.toFixed(2)}</td>
                                  <td>
                                    ${(ticket.cost + ticket.tax).toFixed(2)}
                                  </td>
                                </tr>
                                <tr className={styles.labelCell2}>
                                  <td>New Ticket</td>
                                  <td>${selectedTicket.amount.toFixed(2)}</td>
                                  <td>${selectedTax.toFixed(2)}</td>
                                  <td>${selectedTotal.toFixed(2)}</td>
                                </tr>
                              </tbody>
                            </table>
                            <>
                              {(() => {
                                const oldTotal = ticket.cost + ticket.tax;
                                const newTotal = selectedTotal;
                                const difference = newTotal - oldTotal;

                                let summaryText = "";
                                let summaryClass = "";

                                if (difference > 0) {
                                  summaryText = `Charge: $${difference.toFixed(
                                    2
                                  )}`;
                                  summaryClass = styles.summaryTagCharge;
                                } else if (difference < 0) {
                                  summaryText = `Refund: $${Math.abs(
                                    difference
                                  ).toFixed(2)}`;
                                  summaryClass = styles.summaryTagRefund;
                                } else {
                                  summaryText = "Even Exchange";
                                }

                                return (
                                  <div
                                    className={`${styles.summaryTag} ${summaryClass}`}
                                  >
                                    {summaryText}
                                  </div>
                                );
                              })()}
                            </>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </>
              )}

              <div>
                {selectedTicket && !errorMsg && (
                  <button
                    className={classNames(styles.button, styles.refundBtn)}
                    onClick={() => handleExchangeClick()}
                  >
                    EXCHANGE
                  </button>
                )}
                <button className={styles.button} onClick={onCollapse}>
                  CLOSE
                </button>
              </div>
            </>
          )}
        </>
      </Modal>
    </div>
  );
}

ExchangeTicketModal.propTypes = {
  isOpen: PropTypes.bool,
  onCollapse: PropTypes.func,
  onClick: PropTypes.func,
};
